import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isEmpty } from 'lodash';
import cn from 'classnames';
import {
  setErrorPopUp,
  setPrintNotification,
  closePrintSuccessPopUp,
  closePrint,
} from '@reducers/notification';
import { receiptPrint, receiptEmail, receiptSms } from '@reducers/payment';
import { COUNTRY_CODE } from '@constants';
import { getObjectValue } from '@utility/utills';
import EventEmitter from '@utility/eventSubscribers';
import TABLEDATA from '@components/storeChecks/storeCheckData';
import PrintReceiptLoadingModal from '@components/OrderController/Modals/PrintReceiptLoading';
import FullWithModal from '@components/common/Modal/FullWidthModal';
import { TouchableButton } from '@components/common/Touchable';
import { CUSTOM_MESSAGES } from '@veriables';

const Tablecomp = lazy(() => import('@components/TablePopUp/IndexRevmap'));
const ReceiptScreenPopUp = lazy(() =>
  import('@components/checksCommon/ReceiptScreenPopUpRevamp')
);

const ReceiptPopup = ({
  row,
  onClose,
  serverAppTheme,
  auth,
  deviceInfo,
  userInfo,
  isPrintOpen,
  printData,
  isPrintSuccessOpen,
  receiptEmail,
  receiptSms,
  receiptPrint,
  setPrintNotification,
  closePrintSuccessPopUp,
  closePrint,
  setErrorPopUp,
  loading,
}) => {
  const defaultCountryCode = useMemo(
    () => getObjectValue(() => COUNTRY_CODE[auth?.country].dialCode, 1),
    [auth]
  );
  const phoneNumberMaxLength = useMemo(
    () => getObjectValue(() => COUNTRY_CODE[auth?.country].maxLength, 15),
    [auth]
  );

  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [isReceipt, setIsReceipt] = useState({ isOpen: false, type: '' });
  const [receiptSelection, setReceiptSelection] = useState({ rowData: null, checkArray: [], clearChekedState: () => { } });
  const [inputs, setInputs] = useState({});
  const [validationError, setValidationError] = useState(null);
  const [receiptPrintStatus, setReceiptPrintStatus] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [printSuccess, setPrintSuccess] = useState(false);

  useEffect(() => {
    if (isPrintSuccessOpen) setPrintSuccess(true);
  }, [isPrintSuccessOpen]);

  const handleCountryCodeChange = useCallback(e => setCountryCode(e.value), []);

  const handleReceiptPopUpOpen = useCallback((rowData, checkArray, type, clearChekedState) => {
    setReceiptSelection({ rowData, checkArray, clearChekedState });
    setIsReceipt({ isOpen: true, type });
  }, []);

  const receiptOpen = useCallback(() => onClose(), [onClose]);

  const receiptScreenPopup = useCallback(() => {
    setInputs({});
    setIsReceipt({ isOpen: false, type: '' });
    setValidationError(null);
  }, []);

  const handlePrint = useCallback(
    (data, id, clearChekedState = () => { }) => {
      const selectedTicketData = data?.tickets?.filter(e => id.includes(e.id));

      const promises = selectedTicketData.map(el => {
        const payload = {
          deviceId: deviceInfo.deviceId,
          employeeId: Number(userInfo.id),
          storeId: deviceInfo.storeId,
          employeeCard: userInfo.employeeCard,
          checkHeaderId: row?.id,
          receipt: {
            emailAddress: data?.email || null,
            countryCode: countryCode || null,
            phoneNumber: data?.phone || null,
            ticketId: el?.ticketId || null,
          },
        };

        return receiptPrint(payload)
          .then(res => {
            if (window.ReactNativeWebView) {
              try {
                if (res?.printJobs?.length > 0) {
                  const dataToSend = {
                    check: null,
                    checkHeaderId: 0,
                    control: null,
                    loyalty: null,
                    paymentCheck: null,
                    paymentChecks: null,
                    bytes: res?.printJobs[0]?.bytes,
                    printJobs: [],
                    printerIpAddress: res?.printJobs[0]?.printerIpAddress,
                    printerName: res?.printJobs[0]?.printerName,
                    printerPort: res?.printJobs[0]?.printerPort,
                  };
                  if (!res?.printerAgentBased) {
                    EventEmitter.print(dataToSend);
                    EventEmitter.printData(
                      result => {
                        if (result?.type === 'error') {
                          setReceiptPrintStatus(prev => ({
                            ...prev,
                            [el.ticketId]: {
                              ticketId: el.ticketId,
                              status: 'failed',
                              failed: true,
                              succeded: false,
                              error: result?.message,
                            },
                          }));
                        }
                      },
                      err => {
                        setErrorPopUp({ res: err?.message || err });
                      }
                    );
                  }
                }
              } catch (e) {
                console.log('catch', e);
              }
            } else {
              setReceiptPrintStatus(prev => ({
                ...prev,
                [el.ticketId]: {
                  ticketId: el.ticketId,
                  status: 'success',
                  succeded: true,
                  failed: false,
                },
              }));
            }
          })
          .catch(err => {
            setReceiptPrintStatus(prev => ({
              ...prev,
              [el.ticketId]: {
                ticketId: el.ticketId,
                status: 'failed',
                failed: true,
                succeded: false,
                error: err.message,
              },
            }));
          });
      });

      Promise.all(promises)
        .then(res => setPrintNotification(res))
        .catch(err => setErrorPopUp({ res: err.message }))
        .finally(() => clearChekedState && clearChekedState());
    },
    [deviceInfo, userInfo, row, countryCode, receiptPrint, setPrintNotification, setErrorPopUp]
  );

  const handleReceipt = useCallback(
    data => {
      const { type } = isReceipt;
      let inputError = '';

      if (type === 'email') {
        if (isEmpty(data.email)) {
          inputError = 'Email is required';
        } else if (
          !data.email.match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          )
        ) {
          inputError = 'Enter a valid email';
        } else {
          const { rowData, checkArray, clearChekedState } = receiptSelection;
          const selectedTickets = rowData?.tickets?.filter(e => checkArray.includes(e.id)) || [];
          selectedTickets.forEach(el => {
            receiptEmail({
              ...data,
              deviceId: deviceInfo.deviceId,
              employeeId: Number(userInfo.id),
              storeId: deviceInfo.storeId,
              employeeCard: userInfo.employeeCard,
              checkHeaderId: row?.id,
              receipt: {
                emailAddress: data.email || null,
                countryCode: countryCode || null,
                phoneNumber: null,
                optIn: data.optIn || false,
                ticketId: el?.ticketId || null,
              },
            }).then(() => {
              setReceiptPrintStatus(prev => ({
                ...prev,
                [el.ticketId]: {
                  ticketId: el.ticketId,
                  status: 'success',
                  succeded: true,
                  failed: false,
                },
              }));
            }).catch(err => {
              setReceiptPrintStatus(prev => ({
                ...prev,
                [el.ticketId]: {
                  ticketId: el.ticketId,
                  status: 'failed',
                  failed: true,
                  succeded: false,
                  error: err.message,
                },
              }));
            });
          });
          if (clearChekedState) clearChekedState();
          setInputs({});
          setIsReceipt({ isOpen: false, type: 'email' });
        }
      } else {
        const phone = !isEmpty(data.phone) ? data.phone.replace('-', '') : '';
        if (isEmpty(data.phone)) {
          inputError = 'Enter a phone number';
        } else if (
          Number(countryCode) === 1 &&
          phone.replace('-', '').length < phoneNumberMaxLength
        ) {
          inputError = `Phone number should have ${phoneNumberMaxLength} digits`;
        } else if (
          Number(countryCode) === 1 &&
          phone.replace('-', '').length > phoneNumberMaxLength
        ) {
          inputError = `Phone number should not have more than ${phoneNumberMaxLength} digits`;
        } else {
          const { rowData, checkArray, clearChekedState } = receiptSelection;
          const selectedTickets = rowData?.tickets?.filter(e => checkArray.includes(e.id)) || [];
          selectedTickets.forEach(el => {
            receiptSms({
              ...data,
              deviceId: deviceInfo.deviceId,
              employeeId: Number(userInfo.id),
              storeId: deviceInfo.storeId,
              employeeCard: userInfo.employeeCard,
              checkHeaderId: row?.id,
              receipt: {
                emailAddress: null,
                countryCode: countryCode || null,
                phoneNumber: data.phone || null,
                optIn: data.optIn || false,
                ticketId: el?.ticketId || null,
              },
            }).then(() => {
              setReceiptPrintStatus(prev => ({
                ...prev,
                [el.ticketId]: {
                  ticketId: el.ticketId,
                  status: 'success',
                  succeded: true,
                  failed: false,
                },
              }));
            }).catch(err => {
              setReceiptPrintStatus(prev => ({
                ...prev,
                [el.ticketId]: {
                  ticketId: el.ticketId,
                  status: 'failed',
                  failed: true,
                  succeded: false,
                  error: err.message,
                },
              }));
            });
          });
          if (clearChekedState) clearChekedState();
          setInputs({});
          setIsReceipt({ isOpen: false, type: 'phone' });
        }
      }

      if (inputError.length > 0) {
        setErrorPopUp({ res: inputError });
      }
    },
    [isReceipt, countryCode, phoneNumberMaxLength, receiptSelection, deviceInfo, userInfo, row, receiptEmail, receiptSms, setErrorPopUp]
  );

  const handleErrorPopUp = useCallback(
    id => setErrorPopUp({ res: receiptPrintStatus[id]?.error }),
    [receiptPrintStatus, setErrorPopUp]
  );

  return (
    <>
      <Suspense fallback={null}>
        <Tablecomp
          tHead={TABLEDATA.tHead}
          rowData={row}
          isOpen={true}
          receiptOpen={receiptOpen}
          handlePrint={handlePrint}
          loading={loading}
          handleReceiptPopUpOpen={handleReceiptPopUpOpen}
          countryCode={countryCode}
          orderingPortraitOrientation={false}
          isLandScape={true}
          serverAppTheme={serverAppTheme}
          setSelectedItem={setSelectedItem}
          isMultiSelect
          handleErrorPopUp={handleErrorPopUp}
          receiptPrintStatus={receiptPrintStatus}
          setReceiptPrintStatus={setReceiptPrintStatus}
          setErrorPopUp={setErrorPopUp}
          clearSelectionOnAction
        />
      </Suspense>
      {isReceipt.isOpen && (
        <Suspense fallback={null}>
          <ReceiptScreenPopUp
            onClose={receiptScreenPopup}
            isOpen={isReceipt.isOpen}
            type={isReceipt.type}
            phoneNumberMaxLength={phoneNumberMaxLength}
            handleReceipt={handleReceipt}
            countryCode={countryCode}
            handleCountryCodeChange={handleCountryCodeChange}
            setInputs={setInputs}
            inputs={inputs}
            setError={setValidationError}
            error={validationError}
            isLandScape={true}
            serverAppTheme={serverAppTheme}
            orderingPortraitOrientation={false}
            setSelectedItem={setSelectedItem}
          />
        </Suspense>
      )}
      {isPrintOpen && (
        <PrintReceiptLoadingModal
          isPrintOpen={isPrintOpen}
          printData={printData}
          serverAppTheme={serverAppTheme}
        />
      )}
      {printSuccess && (
        <FullWithModal
          isOpen={printSuccess}
          customClass2={cn(
            'w-100 h-100 p-3 d-flex justify-content-center align-items-center'
          )}
          noBackground
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            width: '486px',
            padding: '1.5rem',
            pointerEvents: 'auto',
            backgroundClip: 'padding-box',
            height: '216px',
            borderRadius: '16px',
            backgroundColor: serverAppTheme?.popUp_bg,
            border: serverAppTheme?.offers_Border,
            boxShadow: serverAppTheme?.popUp_box_shadow,
          }}
        >
          <div className="col-12 fs_18 fw-400 d-flex justify-content-center align-items-center text-center">
            <div>
              <div
                className="text-center pb-25 mb-10"
                style={{ color: serverAppTheme?.primary_text }}
              >
                {CUSTOM_MESSAGES().PRINT_SUCCESS}
              </div>
            </div>
            <div
              className="col-12 px-0 mt-2 position-absolute"
              style={{ bottom: 0, left: 0 }}
            >
              <TouchableButton
                className="col-12 px-0 btn44 btn-ripple fs-24"
                style={{
                  background: serverAppTheme?.secondaryBtn,
                  color: serverAppTheme?.btn_font_color,
                }}
                onClick={() => {
                  setPrintSuccess(false);
                  closePrintSuccessPopUp();
                  closePrint();
                }}
              >
                {CUSTOM_MESSAGES().PRINT_CLOSE}
              </TouchableButton>
            </div>
          </div>
        </FullWithModal>
      )}
    </>
  );
};

const mapStateToProps = ({ oauth, notification, payment }) => ({
  auth: oauth.authInfo,
  deviceInfo: oauth.deviceInformation,
  userInfo: oauth.loggedInUserData,
  isPrintOpen: notification.isPrintOpen,
  printData: notification.printData,
  isPrintSuccessOpen: notification.isPrintSuccessOpen,
  loading: payment.loading
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      receiptEmail,
      receiptSms,
      receiptPrint,
      setPrintNotification,
      closePrintSuccessPopUp,
      closePrint,
      setErrorPopUp,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ReceiptPopup);
